import { ByecodeUIConfigProvider, flex } from '@byecode/ui'
import { getInnerTypeADTValueByStateSchema, PAGE_CONTAINER_HOST, useAtomAction, useAtomData } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import equal from 'fast-deep-equal'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAsyncRetry, useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { appAtom, appRoleAtom, AppSystemPageAtom } from '@/atoms/application/state'
import { fetchSystemDataSourceAtom } from '@/atoms/dataSource/action'
import { fetchBlocksAtom, fetchPageAtom, setPageStateAtom } from '@/atoms/page/action'
import {
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStackAtomFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import SettingAuthentication from '@/components/ApplicationSetting/SettingAuthentication'
import { useCurrentAppId, useCurrentEnvId, useLanguage } from '@/hooks/useApplication'
import { useFetchPageVariable } from '@/hooks/useFetchPageVariable'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

import AccountPage from '../PageDetail/AccountPage'
import { PageLayout } from '../PageDetail/PageLayout'
import { PageLeftPanel } from './LeftPanel'

const Container = styled.div`
    position: relative;
    ${flex};
    height: 100%;
    display: flex;
    overflow: hidden;
`

const Content = styled.div`
    flex: 1;
    display: flex;
    height: 100%;
    width: 100%;
    overflow: hidden;
`

interface PagePathParams {
    pageId: string
    stackId: string
}

const PageMain = ({ pageId, stackId }: PagePathParams) => {
    const [key, setKey] = useState('')
    const { run: fetchSystemDataSource } = useAtomAction(fetchSystemDataSourceAtom)
    const fetchVariables = useFetchPageVariable({ pageId, stackId })
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const userId = useAtomData(appRoleAtom)
    const parseVariable2Field = useParseVariableWithSubscription(undefined, { pageId, stackId })
    const { run: setPageState } = useAtomAction(setPageStateAtom)
    // 获取页面数据变量的记录值
    const {
        value: res,
        retry,
        loading
    } = useAsyncRetry(
        useAtomCallback(
            useCallback(
                async (get, set) => {
                    const pageRes = await set(fetchPageAtom, pageId)
                    if (!pageRes) {
                        return
                    }
                    const [pageVariableParameter] = await Promise.all([fetchVariables(), set(fetchBlocksAtom, { pageId, stackId })])

                    const pageStateParameter = get(pageStatesParameterFamily(pageId))
                    const pageStateWithValue = pageStateParameter?.map(item => ({
                        id: item.id,
                        ...getInnerTypeADTValueByStateSchema(item, {
                            appId,
                            envId,
                            userId,
                            parseVariable2Field
                        })
                    }))
                    setPageState({ stackId, pageState: pageStateWithValue ?? [] })
                    return { pageRes, pageVariableParameter }
                },
                [pageId, fetchVariables, stackId, setPageState, appId, envId, userId, parseVariable2Field]
            )
        ),
        [pageId, stackId]
    )

    const role = useAtomData(appRoleAtom)
    const lastRoleRef = useRef(role)
    const pageStatesParameter = useAtomData(pageStatesParameterFamily(pageId))
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParams = useAtomData(pageQueryParameterFamily(pageId))
    const currentStack = useAtomData(pageStackAtomFamily(stackId))
    const runtimeQueryParams = currentStack?.pageQueryParameters
    const runtimePathParams = currentStack?.pagePathParameters
    const isNotSamePage = res?.pageRes.id !== pageId

    // 栈运行时路由参数变化 → 仅重新获取变量（不卸载页面，保留浮层等状态）
    const prevRouteRef = useRef({ stackId, runtimeQueryParams, runtimePathParams })
    useUpdateEffect(() => {
        if (equal(prevRouteRef.current, { stackId, runtimeQueryParams, runtimePathParams })) {
            return
        }
        prevRouteRef.current = { stackId, runtimeQueryParams, runtimePathParams }
        if (isNotSamePage) {
            return
        }
        fetchVariables()
    }, [stackId, runtimeQueryParams, runtimePathParams, isNotSamePage, fetchVariables])

    // 页面数据变量的配置项 schema 变化时，重新获取页面变量
    const latestPageVariableRef = useRef({ pageVariableSchemas, pagePathParameter, pageQueryParams, pageStatesParameter })
    useUpdateEffect(() => {
        if (
            equal(latestPageVariableRef.current, { pageVariableSchemas, pagePathParameter, pageQueryParams, pageStatesParameter }) &&
            lastRoleRef.current === role
        ) {
            return
        }

        latestPageVariableRef.current = { pageVariableSchemas, pagePathParameter, pageQueryParams, pageStatesParameter }

        lastRoleRef.current = role

        if (isNotSamePage) {
            return
        }

        fetchVariables()
    }, [role, isNotSamePage, pageVariableSchemas, pagePathParameter, pageQueryParams, pageStatesParameter])

    const handleFreshPage = useCallback(() => {
        fetchSystemDataSource()
        retry()
        setKey(nanoid())
    }, [fetchSystemDataSource, retry])

    return (
        <PageLayout
            key={key}
            pageId={pageId}
            stackId={stackId}
            loading={loading || res?.pageRes?.id !== pageId}
            onFreshPage={handleFreshPage}
        />
    )
}

const handleDisableBrowserOperation = (e: MouseEvent) => {
    e.preventDefault()
}

export const Page: React.FC<PagePathParams> = props => {
    const { stackId } = props
    const browserOperation = useAtomData(
        appAtom,
        useCallback(s => s?.config?.browserOperation, [])
    )
    const isShowSystemPage = useAtomData(AppSystemPageAtom)
    const locale = useLanguage()
    // 禁用浏览器操作限制
    useEffect(() => {
        const pageContainer = document.querySelector<HTMLDivElement>(PAGE_CONTAINER_HOST)
        if (!pageContainer) {
            return
        }
        if (browserOperation?.disableBrowserMenu) {
            pageContainer.addEventListener('contextmenu', handleDisableBrowserOperation)
        }

        if (browserOperation?.disableSelectText) {
            pageContainer.style.webkitUserSelect = 'none' // Safari / WebKit
            pageContainer.style.userSelect = 'none'
        }

        return () => {
            if (browserOperation?.disableBrowserMenu) {
                pageContainer.removeEventListener('contextmenu', handleDisableBrowserOperation)
            }
            if (browserOperation?.disableSelectText) {
                pageContainer.style.webkitUserSelect = ''
                pageContainer.style.userSelect = ''
            }
        }
    }, [stackId, isShowSystemPage, browserOperation?.disableBrowserMenu, browserOperation?.disableSelectText])

    return (
        <Container>
            <Content style={{ width: isShowSystemPage ? 'calc(100% - 560px)' : 'auto' }}>
                <PageLeftPanel />
                <ByecodeUIConfigProvider locale={locale}>
                    {isShowSystemPage ? <AccountPage /> : <PageMain {...props} />}
                </ByecodeUIConfigProvider>
            </Content>

            {isShowSystemPage && <SettingAuthentication />}
        </Container>
    )
}
